"use client";

import Link from "next/link";
import ContentWrapper from "../common/wrappers/contentWrapper";
import { LayoutWrapper } from "../common/wrappers/layout_wrapper";
import { Heading } from "../common/typography/Heading";
import Typography from "../common/typography";
import Image from "next/image";
import TableOfContents from "./TableOfContents";
import JsonLd from "../JsonLd";
import Blogs from "../home/Blogs";
import { BlogpageUIMapper, FlattenedPost } from "@/services/blog/blog.mapper";
import AiLink from "./AiLink";
import { convertHTMLToRenderableContent } from "@/services/blog/blog.utilities";

import { useEffect, useRef } from "react";
import { HyvorCommentingPlugin } from "./HyvorCommentingPlugin";
import { CitiesInThisState } from "../location-page/cities-in-this-state";
import StudyLink from "./StudyLink";
import FeaturedCitiesExplorer from "../home/CardDetailingServiceAreas/FeaturedCitiesExplorer";

type StudiesSlugPageLayoutProps = {
  slug: string;
  /**
   * Absolute URL of the current post. Must be computed in a Server Component
   * using the server-only SITE_URL env var, then passed as a prop. Computing
   * it inside this client component would use the client-side `process.env`
   * inline (which differs from the server value) and break hydration.
   */
  currentUrl: string;
  press?: boolean;
  post: FlattenedPost;
  countrySlug?: string;
  studiesCarouselLocations: {
    usa: {
      city: string;
      slug: string;
      region?: string | null;
    }[];
    ca: {
      city: string;
      slug: string;
      region?: string | null;
    }[];
  };
  featuredCitiesData: {
    usa: {
      province: string;
      slug: string;
    }[];
    ca: {
      province: string;
      slug: string;
    }[];
  };
  breadcrumbs: {
    "@context": string;
    "@type": string;
    itemListElement: {
      "@type": string;
      position: number;
      name: string;
      item: string;
    }[];
  };
  jsonLd: object | object[] | null;

  relatedPostSection: {
    title: string;
    description: string;
    posts: Pick<BlogpageUIMapper, "posts">;
  };
};
export function StudySlugPageLayout({
  post,
  jsonLd,
  breadcrumbs,
  press = false,
  ...props
}: StudiesSlugPageLayoutProps) {
  const processedHtml = convertHTMLToRenderableContent(post.post);

  return (
    <LayoutWrapper className="py-50px sm:py-sections-mobile-70">
      <ContentWrapper fullWidth className=" pb-6 !overflow-visible">
        <article
          data-testid={`blog-article-container`}
          className="max-w-none pb-sections-desktop-120 contentContainer"
        >
          <header className="mb-6">
            <Heading
              dataTestId={`blog-title`}
              className="mt-2.5"
              as="h1"
              size="h1"
            >
              {post.title}
            </Heading>

            {/* <Typography */}
            {/*   dataTestId="blog-summary" */}
            {/*   className="mt-5" */}
            {/*   color="copyGray" */}
            {/*   as="h6" */}
            {/*   size="heading6" */}
            {/* > */}
            {/*   {post.post_summary} */}
            {/* </Typography> */}

            {post.thumbnail_image?.filename_disk ? (
              <picture data-testid={`blog-featured-image`}>
                {/* image for desktop */}
                <source
                  srcSet={`https://images.pandahub.com/cdn-cgi/image/quality=70,format=avif/${post.thumbnail_image.filename_disk}`}
                  media="(max-width: 83rem)"
                />

                <Image
                  src={
                    post.featured_image?.filename_disk ||
                    "7b796cf1-e866-42d2-9fc1-e55ceedbb845.jpg"
                  }
                  alt={post.featured_image?.description || post.title}
                  width={1280}
                  height={640}
                  rel="preload"
                  fetchPriority="high"
                  priority
                  loading="eager"
                  className={`object-cover  mt-[2.1875rem] sm:mt-[3.125rem] w-full  aspect-auto max-cw:max-h-[480px]`}
                />
              </picture>
            ) : (
              <Image
                data-testid={`blog-featured-image`}
                rel="preload"
                priority
                loading="eager"
                src={
                  post.featured_image?.filename_disk ||
                  "7b796cf1-e866-42d2-9fc1-e55ceedbb845.jpg"
                }
                alt={post.featured_image?.description || post.title}
                width={1280}
                height={640}
                className="object-cover mt-[2.1875rem] sm:mt-[3.125rem] w-full h-[14.375rem] sm:h-[28.75rem] rounded-3xl"
              />
            )}

            <section className="flex justify-between lg:flex-row flex-col pt-5 lg:pt-5">
              <div className="flex flex-col">
                <div className="flex flex-row gap-5 pb-5 ">
                  {/* author byline */}
                  <header
                    data-testid={"blog-block-author"}
                    className="flex gap-2.5"
                  >
                    {post.author?.profile_picture ? (
                      post.author.slug ? (
                        <Link
                          data-testid={"blog-block-author-image-as-link"}
                          href={`/authors/${post.author.slug}`}
                        >
                          <Image
                            src={
                              post.author.profile_picture
                                .filename_disk as string
                            }
                            alt={post.author.name}
                            width={40}
                            height={40}
                            className="rounded-full"
                          />
                        </Link>
                      ) : (
                        <Image
                          data-testid={"blog-block-author-image"}
                          src={
                            post.author.profile_picture.filename_disk as string
                          }
                          alt={post.author.name}
                          width={40}
                          height={40}
                          className="rounded-full !w-[2.5rem] !h-[2.5rem]"
                        />
                      )
                    ) : (
                      <div className="w-10 h-10 bg-perplexity rounded-full text-white flex items-center justify-center">
                        {post.author?.name?.substring(0, 2)?.toUpperCase() ||
                          "PT"}
                      </div>
                    )}

                    <div className="lg:mb-0">
                      <Typography size="body2" color="darkGray">
                        Written by:
                      </Typography>
                      {post?.author?.slug ? (
                        <Link
                          href={`/authors/${post?.author?.slug}`}
                          className="flex flex-row gap-1"
                          data-testid={"blog-block-author-name-as-link"}
                        >
                          <Typography size="body1">
                            {post.author?.name ?? "Panda Hub Team"}
                          </Typography>
                        </Link>
                      ) : (
                        <div className="flex flex-row gap-1">
                          <Typography
                            dataTestId="blog-block-author-name"
                            size="body1"
                          >
                            {post.author?.name ?? "Panda Hub Team"}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </header>

                  {/* <header */}
                  {/*   data-testid="blog-block-reviewer" */}
                  {/*   className="flex gap-2.5" */}
                  {/* > */}
                  {/*   <div className="w-10 h-10"> */}
                  {/*     <Image */}
                  {/*       data-testid="blog-block-reviewer-image" */}
                  {/*       src={"eaccf8b6-e04e-476f-a57c-0f622b38409f.png"} */}
                  {/*       alt={"pandahub editorial team"} */}
                  {/*       width={40} */}
                  {/*       height={40} */}
                  {/*       className="rounded-full" */}
                  {/*     /> */}
                  {/*   </div> */}
                  {/*   <div className="lg:mb-0"> */}
                  {/*     <Typography size="body2" color="darkGray"> */}
                  {/*       Reviewed By: */}
                  {/*     </Typography> */}
                  {/*     <Link href="/editorial-policy" className="underline"> */}
                  {/*       <Typography */}
                  {/*         dataTestId="blog-block-reviewer-name" */}
                  {/*         size="body1" */}
                  {/*       > */}
                  {/*         Editorial Team */}
                  {/*       </Typography> */}
                  {/*     </Link> */}
                  {/*   </div> */}
                  {/* </header> */}
                </div>

                {/* PUBLISHED AND UPDATED */}
                {/* {post.published_at && ( */}
                {/*   <Typography */}
                {/*     dataTestId="blog-published-date" */}
                {/*     size="unresponsiveBody2" */}
                {/*     color="copyGray" */}
                {/*     className="mt-5px" */}
                {/*   > */}
                {/*     Published:{" "} */}
                {/*     {new Date(post.published_at).toLocaleDateString("en-us", { */}
                {/*       day: "2-digit", */}
                {/*       year: "numeric", */}
                {/*       month: "long", */}
                {/*       timeZone: "UTC", */}
                {/*     })} */}
                {/*   </Typography> */}
                {/* )} */}
                {/* {post.date_updated && ( */}
                {/*   <Typography */}
                {/*     dataTestId="blog-updated-date" */}
                {/*     size="unresponsiveBody2" */}
                {/*     color="copyGray" */}
                {/*     className="pb-35px lg:pb-0" */}
                {/*   > */}
                {/*     Updated:{" "} */}
                {/*     {new Date(post.date_updated).toLocaleDateString("en-us", { */}
                {/*       day: "2-digit", */}
                {/*       year: "numeric", */}
                {/*       month: "long", */}
                {/*       timeZone: "UTC", */}
                {/*     })} */}
                {/*   </Typography> */}
                {/* )} */}
              </div>

              <StudyLink currentUrl={props.currentUrl} blogTitle={post.title} />
            </section>
          </header>

          {/* <div className="flex relative sm:justify-evenly gap-5 max-w-full mt-20"> */}
          <div className="max-w-full">
            <section
              data-testid={`blog-body-content`}
              className={`blogContainer`}
              dangerouslySetInnerHTML={{ __html: processedHtml.html }}
            />

            {/*   {post.author && ( */}
            {/*     <div */}
            {/*       data-testid={"blog-author-detail-block"} */}
            {/*       className="w-full sm:max-h-full p-5 bg-bgColor mt-9 rounded-[.875rem] flex lg:flex-row flex-col gap-3.5" */}
            {/*     > */}
            {/*       <div */}
            {/*         data-testid={"blog-author-detail-block-image"} */}
            {/*         className="w-100px h-100px" */}
            {/*       > */}
            {/*         <Image */}
            {/*           src={post.author?.profile_picture?.filename_disk || ""} */}
            {/*           alt={post.author?.profile_picture?.description || ""} */}
            {/*           width={100} */}
            {/*           height={100} */}
            {/*           className="rounded-full object-cover max-w-100px max-h-100px" */}
            {/*         /> */}
            {/*       </div> */}
            {/*       <div> */}
            {/*         <Heading */}
            {/*           dataTestId={"blog-author-detail-block-name"} */}
            {/*           as="p" */}
            {/*           size="h5" */}
            {/*         > */}
            {/*           {post.author?.name} */}
            {/*         </Heading> */}
            {/*         <Typography */}
            {/*           dataTestId={"blog-author-detail-block-role"} */}
            {/*           as="p" */}
            {/*           size="body2" */}
            {/*           color="copyGray" */}
            {/*         > */}
            {/*           {post.author?.role} */}
            {/*         </Typography> */}
            {/*         <Typography */}
            {/*           dataTestId={"blog-author-detail-block-subline"} */}
            {/*           as="p" */}
            {/*           size="unresponsiveBody1" */}
            {/*           className="mt-4" */}
            {/*         > */}
            {/*           {post.author?.subline} */}
            {/*         </Typography> */}
            {/*         {post.author.slug && ( */}
            {/*           <Link */}
            {/*             data-testid={"blog-author-detail-block-read-more-link"} */}
            {/*             href={`/authors/${post.author?.slug}`} */}
            {/*           > */}
            {/*             <Typography */}
            {/*               size="body1" */}
            {/*               color="main" */}
            {/*               className="underline mt-4" */}
            {/*             > */}
            {/*               Read more */}
            {/*             </Typography> */}
            {/*           </Link> */}
            {/*         )} */}
            {/*       </div> */}
            {/*     </div> */}
            {/*   )} */}
            {/**/}
            {/*   {post.slug && <HyvorCommentingPlugin pageId={post.slug} />} */}
            {/* </div> */}
            {/* TABLE OF CONTENTs */}
            {/* {processedHtml.headings.length > 0 && ( */}
            {/*   <div className="shrink-0 lg:block hidden"> */}
            {/*     <TableOfContents headings={processedHtml.headings} /> */}
            {/*   </div> */}
            {/* )} */}
          </div>

          <JsonLd data={[breadcrumbs, jsonLd].filter(Boolean) as object[]} />
        </article>
      </ContentWrapper>

      {(props.featuredCitiesData.ca.length > 0 ||
        props.featuredCitiesData.usa.length > 0) && (
        <ContentWrapper fullWidth className="pb-sections-desktop mt-sections-mobile-70">
          <div className="max-w-848px mb-10 contentContainer">
            <Heading size="h2" as="h2">Book mobile car detailing near you</Heading>
            <Heading size="h6" as="p" color="copyGray" className="mt-5">Panda Hub brings professional detailing to your driveway in 136+ cities across the United States and Canada. Find your city below.</Heading>
          </div>
          <FeaturedCitiesExplorer
            usa={props.featuredCitiesData.usa}
            ca={props.featuredCitiesData.ca}
            locations={props.studiesCarouselLocations}
          />
        </ContentWrapper>
      )}

      {/* {props.featuredCities && post.location_carousel && ( */}
      {/*   <CitiesInThisState */}
      {/*     noPadding */}
      {/*     countrySlug={props.countrySlug} */}
      {/*     featuredCities={props.featuredCities} */}
      {/*   /> */}
      {/* )} */}
      {/**/}
      {/* {props.relatedPostSection && ( */}
      {/*   <ContentWrapper fullWidth className="mt-sections-desktop-120"> */}
      {/*     <Blogs */}
      {/*       showButton={!press} */}
      {/*       press={press} */}
      {/*       title={props.relatedPostSection.title} */}
      {/*       description={ */}
      {/*         props.relatedPostSection.description || */}
      {/*         "Enhance your car care expertise with our related blog posts. Find valuable tips and insights to keep your vehicle in top condition." */}
      {/*       } */}
      {/*       {...props.relatedPostSection.posts} */}
      {/*     /> */}
      {/*   </ContentWrapper> */}
      {/* )} */}
    </LayoutWrapper>
  );
}
